import { useCallback, useEffect, useState } from 'react';
import { api, ErrorApi, type EnlaceInforme } from '../api';
import { fechaCorta, describirVigencia } from '../enlaces';
import { Tabla } from './Tabla';

/**
 * Enlaces públicos al informe.
 *
 * La URL se muestra una sola vez, al crearla, igual que el secreto del webhook
 * y los códigos de respaldo: quien tenga el enlace ve los datos del cliente sin
 * pedir sesión, así que una pantalla que lo pueda volver a mostrar es una
 * pantalla desde la que se lo lleva cualquiera con acceso a la sesión.
 *
 * Lo que sí queda visible es todo lo demás —quién lo creó, cuándo vence,
 * cuántas veces se abrió— porque es lo que permite decidir si conviene
 * revocarlo.
 */
export function Enlaces({ desde, onError }: { desde: string; onError: (err: unknown) => void }) {
  const [enlaces, setEnlaces] = useState<EnlaceInforme[] | null>(null);
  const [nuevo, setNuevo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      setEnlaces((await api.enlacesInforme()).enlaces);
    } catch (err) {
      onError(err);
    }
  }, [onError]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function crear(): Promise<void> {
    setCreando(true);
    setError(null);
    setNuevo(null);
    try {
      const creado = await api.crearEnlaceInforme(desde);
      setNuevo(creado.url);
      await cargar();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'no se pudo crear el enlace');
    } finally {
      setCreando(false);
    }
  }

  async function revocar(id: string): Promise<void> {
    setError(null);
    try {
      await api.revocarEnlaceInforme(id);
      setNuevo(null);
      await cargar();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'no se pudo revocar');
    }
  }

  if (!enlaces) return null;

  return (
    <section className="enlaces-informe">
      <div className="analisis-cabecera">
        <h3>Enlaces compartidos</h3>
        <button onClick={() => void crear()} disabled={creando}>
          {creando ? 'Creando…' : 'Crear enlace'}
        </button>
      </div>

      <p className="tenue">
        Abre sin pedir sesión, así que cualquiera con la dirección ve el informe. Vence a los siete
        días, se puede revocar acá, y cada apertura queda en la bitácora. El informe queda
        congelado: refleja la ventana de cuando se creó y no cambia después.
      </p>

      {error && <p className="error">{error}</p>}

      {nuevo && (
        <div className="secreto-nuevo">
          <h4>Copia el enlace ahora</h4>
          <p className="tenue">Es la única vez que se muestra. Si lo pierdes, revocá y creá otro.</p>
          <code>{nuevo}</code>
          <button onClick={() => void navigator.clipboard?.writeText(nuevo)}>Copiar</button>
        </div>
      )}

      {enlaces.length === 0 ? (
        <p className="vacio">No hay enlaces vigentes.</p>
      ) : (
        <Tabla className="salud">
          <thead>
            <tr>
              <th>Creado por</th>
              <th>Ventana del informe</th>
              <th className="num">Aperturas</th>
              <th>Vence</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {enlaces.map((e) => (
              <tr key={e.id}>
                <td>
                  {e.creadoPor}
                  <span className="tenue"> · {fechaCorta(e.creado)}</span>
                </td>
                <td className="tenue">
                  {fechaCorta(e.ventana.desde)} — {fechaCorta(e.ventana.hasta)}
                </td>
                <td className="num">
                  {e.aperturas}
                  {e.ultimaApertura && (
                    <span className="tenue"> · última {fechaCorta(e.ultimaApertura)}</span>
                  )}
                </td>
                <td>{describirVigencia(e.expira)}</td>
                <td className="num">
                  <button className="secundario" onClick={() => void revocar(e.id)}>
                    Revocar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Tabla>
      )}
    </section>
  );
}
